import { useState, useEffect } from 'react';
import { Dialog, MobileDialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { HeadingLarge, HeadingMedium, BodyMedium, BodySmall, Caption } from '@/components/ui/typography';
import { Zap, Plus, Minus, CheckCircle2, Loader2 } from 'lucide-react';
import { formatPrice } from '@/utils/billing/billingFormatters';
import { supabase } from '@/integrations/supabase/client';
import { useTranslatedToast } from '@/hooks/useTranslatedToast';
import { usePricingConfig } from '@/hooks/usePricingConfig';
import { useTranslation } from '@/hooks/useTranslation';

interface OneTimeCreditPurchaseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: string;
  companyName: string;
  creditPrice: number; // Price per credit in cents (from tier)
  currentBalance: number; // For context
  onSuccess: () => void; // Callback to refresh billing data
}

export function OneTimeCreditPurchaseDialog({
  open,
  onOpenChange,
  companyId,
  companyName,
  creditPrice,
  currentBalance,
  onSuccess,
}: OneTimeCreditPurchaseDialogProps) {
  const [selectedCredits, setSelectedCredits] = useState(50);
  const [inputValue, setInputValue] = useState('50');
  const [isProcessing, setIsProcessing] = useState(false);
  const [customMode, setCustomMode] = useState(false);
  
  const { getCost } = usePricingConfig();
  const { t } = useTranslation('pages');
  const toast = useTranslatedToast();

  // Quick-select packages with translated badges
  const QUICK_SELECT_PACKAGES = [
    { credits: 50, label: '50', badge: t('billing.badge_popular') },
    { credits: 100, label: '100', badge: null },
    { credits: 200, label: '200', badge: null },
    { credits: 500, label: '500', badge: null },
    { credits: 1000, label: '1000', badge: t('billing.badge_best_value') },
  ];

  const MIN_CREDITS = 20;
  const MAX_CREDITS = 5000;
  const STEP = 10;

  // Sync slider and input
  useEffect(() => {
    setInputValue(selectedCredits.toString());
  }, [selectedCredits]);

  const handleQuickSelect = (credits: number) => {
    setSelectedCredits(credits);
    setCustomMode(false);
  };

  const handleSliderChange = (value: number[]) => {
    const credits = value[0];
    setSelectedCredits(credits);
    setCustomMode(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    
    const numValue = parseInt(value);
    if (!isNaN(numValue)) {
      const clampedValue = Math.max(MIN_CREDITS, Math.min(MAX_CREDITS, numValue));
      setSelectedCredits(clampedValue);
      setCustomMode(true);
    }
  };

  const handleInputBlur = () => {
    const numValue = parseInt(inputValue);
    if (isNaN(numValue) || numValue < MIN_CREDITS) {
      setSelectedCredits(MIN_CREDITS);
      setInputValue(MIN_CREDITS.toString());
    } else if (numValue > MAX_CREDITS) {
      setSelectedCredits(MAX_CREDITS);
      setInputValue(MAX_CREDITS.toString());
    } else {
      // Round to nearest step
      const rounded = Math.round(numValue / STEP) * STEP;
      setSelectedCredits(rounded);
      setInputValue(rounded.toString());
    }
  };

  const handleIncrement = () => {
    const newValue = Math.min(MAX_CREDITS, selectedCredits + STEP);
    setSelectedCredits(newValue);
    setCustomMode(true);
  };

  const handleDecrement = () => {
    const newValue = Math.max(MIN_CREDITS, selectedCredits - STEP);
    setSelectedCredits(newValue);
    setCustomMode(true);
  };

  const totalCost = (selectedCredits * creditPrice) / 100; // Convert cents to dollars
  const newBalance = currentBalance + selectedCredits;

  // Calculate what can be done with credits
  const aiInterviewCost = getCost('ai_interview');
  const aiInterviews = aiInterviewCost > 0 ? Math.floor(selectedCredits / aiInterviewCost) : 0;

  const handlePurchase = async () => {
    if (selectedCredits < 20 || selectedCredits > 5000) {
      toast.raw.error(
        selectedCredits < 20 
          ? t('billing.minimum_purchase_error', { count: MIN_CREDITS })
          : t('billing.maximum_purchase_error', { count: MAX_CREDITS })
      );
      return;
    }

    setIsProcessing(true);

    try {
      // Call unified edge function to create Stripe checkout session
      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: {
          companyId,
          paymentType: 'one_time',
          creditQuantity: selectedCredits,
          successUrl: `${window.location.origin}/account/billing?success=true&credits=${selectedCredits}`,
          cancelUrl: `${window.location.origin}/account/billing?canceled=true`,
        },
      });

      if (error) throw error;

      if (data?.sessionUrl) {
        // Use the same redirect logic as subscription purchases
        const newWindow = window.open(data.sessionUrl, '_blank');
        
        if (!newWindow || newWindow.closed) {
          // Popup blocked, redirect in current window after brief delay
          toast.raw.info('Opening payment page...');
          setTimeout(() => {
            window.location.href = data.sessionUrl;
          }, 1000);
        } else {
          // Successfully opened in new window, close dialog
          onOpenChange(false);
          toast.raw.success(t('billing.redirecting_to_payment'));
        }
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (error) {
      console.error('Failed to initiate credit purchase:', error);
      toast.raw.error(t('billing.purchase_failed'));
      setIsProcessing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <MobileDialogContent className="md:max-w-[600px]">
        <DialogHeader className="pb-4 md:pb-6">
          <DialogTitle className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-orange-400 via-pink-500 to-purple-600 flex items-center justify-center">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <HeadingLarge>{t('billing.add_credits_title')}</HeadingLarge>
          </DialogTitle>
          <DialogDescription>
            {t('billing.purchase_credits_description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4 md:flex-1 md:min-h-0 md:overflow-y-auto md:max-h-[65vh] overscroll-contain">
          {/* Quick Select */}
          <div className="space-y-3">
            <BodyMedium className="font-medium">{t('billing.quick_select_label')}</BodyMedium>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              {QUICK_SELECT_PACKAGES.map((pkg) => (
                <Button
                  key={pkg.credits}
                  variant={selectedCredits === pkg.credits && !customMode ? 'default' : 'outline'}
                  className="h-auto flex flex-col items-center gap-1 py-3 relative"
                  onClick={() => handleQuickSelect(pkg.credits)}
                >
                  {pkg.badge && (
                    <Badge 
                      variant="secondary" 
                      className="absolute -top-2 -right-2 text-[10px] px-1.5 py-0.5 bg-primary text-primary-foreground"
                    >
                      {pkg.badge}
                    </Badge>
                  )}
                  <span className="text-2xl font-bold">{pkg.label}</span>
                  <Caption className="text-muted-foreground">{t('billing.credits_label')}</Caption>
                </Button>
              ))}
            </div>
          </div>

          {/* Custom Amount */}
          <div className="space-y-3">
            <BodyMedium className="font-medium">{t('billing.custom_amount_label')}</BodyMedium>
            
            {/* Input with +/- buttons */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={handleDecrement}
                disabled={selectedCredits <= MIN_CREDITS}
                aria-label={t('billing.decrease_credits')}
              >
                <Minus className="h-4 w-4" />
              </Button>
              
              <div className="flex-1 relative">
                <Input
                  type="number"
                  value={inputValue}
                  onChange={handleInputChange}
                  onBlur={handleInputBlur}
                  min={MIN_CREDITS}
                  max={MAX_CREDITS}
                  step={STEP}
                  className="text-center text-lg font-semibold pr-16"
                  aria-label="Credit amount"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                  {t('billing.credits_label')}
                </span>
              </div>

              <Button
                variant="outline"
                size="icon"
                onClick={handleIncrement}
                disabled={selectedCredits >= MAX_CREDITS}
                aria-label={t('billing.increase_credits')}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>

            {/* Slider */}
            <div className="px-2">
              <Slider
                value={[selectedCredits]}
                onValueChange={handleSliderChange}
                min={MIN_CREDITS}
                max={MAX_CREDITS}
                step={STEP}
                className="w-full"
                aria-label="Select credit amount"
              />
              <div className="flex justify-between mt-1">
                <Caption className="text-muted-foreground">{MIN_CREDITS}</Caption>
                <Caption className="text-muted-foreground">{MAX_CREDITS}</Caption>
              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
            <HeadingMedium>{t('billing.order_summary')}</HeadingMedium>
            
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <BodySmall className="text-muted-foreground">{t('billing.credits_to_add')}</BodySmall>
                <BodyMedium className="font-semibold">{selectedCredits.toLocaleString()}</BodyMedium>
              </div>
              
              <div className="flex justify-between items-center">
                <BodySmall className="text-muted-foreground">{t('billing.price_per_credit')}</BodySmall>
                <BodyMedium>{formatPrice(creditPrice)}</BodyMedium>
              </div>
              
              <div className="h-px bg-border my-2" />
              
              <div className="flex justify-between items-center">
                <BodyMedium className="font-semibold">{t('billing.total')}</BodyMedium>
                <HeadingLarge className="text-primary">${totalCost.toFixed(2)}</HeadingLarge>
              </div>

              <div className="flex justify-between items-center pt-2">
                <BodySmall className="text-muted-foreground">{t('billing.new_balance_label')}</BodySmall>
                <BodyMedium className="font-semibold">
                  {currentBalance.toLocaleString()} → {newBalance.toLocaleString()} {t('billing.credits_label')}
                </BodyMedium>
              </div>
            </div>
          </div>

          {/* Use Case Examples */}
          <div className="rounded-lg border bg-muted/20 p-4 space-y-2">
            <BodySmall className="text-muted-foreground flex items-center gap-2">
              <Zap className="h-4 w-4" />
              {t('billing.credits_usage_examples', { count: selectedCredits })}
            </BodySmall>
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0" />
                <BodySmall>
                  {t('billing.ai_interviews_cost', { count: aiInterviews, cost: aiInterviewCost })}
                </BodySmall>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isProcessing}
              className="sm:flex-1"
            >
              {t('billing.cancel')}
            </Button>
            <Button
              onClick={handlePurchase}
              disabled={isProcessing || selectedCredits < MIN_CREDITS}
              className="sm:flex-1 bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('billing.processing')}
                </>
              ) : (
                <>
                  {t('billing.purchase_credits_button', { count: selectedCredits })}
                </>
              )}
            </Button>
          </div>

          {/* Validation Messages */}
          {selectedCredits < MIN_CREDITS && (
            <Caption className="text-destructive text-center">
              {t('billing.minimum_purchase_error', { count: MIN_CREDITS })}
            </Caption>
          )}
          {selectedCredits > MAX_CREDITS && (
            <Caption className="text-destructive text-center">
              {t('billing.maximum_purchase_error', { count: MAX_CREDITS })}
            </Caption>
          )}
        </div>
      </MobileDialogContent>
    </Dialog>
  );
}
